import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Plus, Loader2, AlertCircle, X, Building2, Plane,
} from 'lucide-react';
import api from '@utils/api';
import { canSeeStation, reachOf } from '@utils/stationScope';

// The registry every platform reads. A station added here reaches Training
// within the hour and HR and CMMS as they adopt the feed, so the copy on this
// page says so plainly — it is not a local list.

const KINDS = [
  { value: 'airport', label: 'Airport', icon: Plane,      hint: 'An operational station with an IATA code' },
  { value: 'office',  label: 'Office',  icon: Building2,  hint: 'A base with staff but no ramp — head office' },
];

// Offered as a shortcut, not a constraint: the field accepts any IANA zone and
// the server validates it. These are the ones GroundLink actually operates in,
// and the islands genuinely differ from the mainland.
const COMMON_TZ = ['Europe/Lisbon', 'Atlantic/Madeira', 'Atlantic/Azores'];

const EMPTY = { code: '', name: '', timezone: 'Europe/Lisbon', kind: 'airport' };

function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl p-6 ${className}`}
      style={{
        background:     'rgba(10,16,35,0.9)',
        border:         '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(24px)',
        boxShadow:      '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(45,125,70,0.06)',
      }}
    >
      {children}
    </div>
  );
}

function StationModal({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm]     = useState(isEdit ? { ...EMPTY, ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  function set(key, value) { setForm((f) => ({ ...f, [key]: value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        // `code` is deliberately absent: it is the join key HR and CMMS still
        // match on as free text, so renaming it would orphan their records.
        await api.patch(`/stations/${initial.id}`, {
          name:      form.name,
          timezone:  form.timezone,
          kind:      form.kind,
          is_active: form.is_active,
        });
      } else {
        await api.post('/stations', form);
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-white">
              {isEdit ? `Edit ${initial.code}` : 'Add station'}
            </h2>
            <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors">
              <X size={18} />
            </button>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">Code</label>
              <input
                value={form.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                disabled={isEdit}
                required
                maxLength={4}
                placeholder="OPO"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white
                  placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-glk-green
                  disabled:opacity-40 disabled:cursor-not-allowed font-mono tracking-wider"
              />
              {isEdit && (
                <p className="mt-1 text-[11px] text-white/35 leading-snug">
                  The code cannot change: HR and CMMS still match their records on it as text.
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">Name</label>
              <input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                required
                placeholder="Aeroporto Francisco Sá Carneiro"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white
                  placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-glk-green"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">Timezone</label>
              <input
                value={form.timezone}
                onChange={(e) => set('timezone', e.target.value)}
                required
                list="tz-options"
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white
                  focus:outline-none focus:ring-2 focus:ring-glk-green font-mono"
              />
              <datalist id="tz-options">
                {COMMON_TZ.map((tz) => <option key={tz} value={tz} />)}
              </datalist>
              <p className="mt-1 text-[11px] text-white/35 leading-snug">
                Training calculates certificate expiry against this. The Azores are an
                hour behind the mainland — a record due &ldquo;today&rdquo; is a different moment there.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-white/60 mb-1.5">Type</label>
              <div className="grid grid-cols-2 gap-2">
                {KINDS.map(({ value, label, icon: Icon, hint }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => set('kind', value)}
                    className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      form.kind === value
                        ? 'border-glk-green/60 bg-glk-green/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium text-white">
                      <Icon size={14} />{label}
                    </span>
                    <span className="block mt-0.5 text-[10px] text-white/40 leading-tight">{hint}</span>
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-white/35 leading-snug">
                Training only tracks airports. Staff based at an office are reported as
                out of scope, not as an error.
              </p>
            </div>

            {isEdit && (
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active !== false}
                  onChange={(e) => set('is_active', e.target.checked)}
                  className="rounded border-white/20 bg-white/5"
                />
                <span className="text-sm text-white/70">Active</span>
                <span className="text-[11px] text-white/35">
                  — closing a station deactivates it everywhere; nothing is deleted
                </span>
              </label>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-glk-green
                  px-4 py-2 text-sm font-semibold text-white hover:bg-glk-green/90
                  disabled:opacity-50 transition-colors"
              >
                {saving && <Loader2 size={15} className="animate-spin" />}
                {isEdit ? 'Save changes' : 'Add station'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}

export function Stations() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [editing, setEditing]   = useState(null);
  const [users, setUsers]       = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // ?all=1 — an admin screen must show closed stations, or reopening one
      // is impossible from here.
      const { data } = await api.get('/stations?all=1');
      setStations(data.stations || []);
      // Counts and the access list need the users; a failure here must not hide the stations.
      api.get('/users').then((r) => setUsers(r.data)).catch(() => setUsers([]));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load stations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen px-6 py-10" style={{ background: '#070B18' }}>
      <div className="mx-auto max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white/70 transition-colors mb-6"
        >
          <ArrowLeft size={15} /> Back
        </Link>

        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-white">
              <MapPin size={19} /> Stations
            </h1>
            <p className="mt-1 text-sm text-white/45 max-w-xl leading-snug">
              Where GroundLink operates. Every platform reads this list — Training picks
              up changes within the hour. Stations are never deleted, only deactivated.
            </p>
          </div>
          <button
            onClick={() => setEditing({})}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-glk-green px-3.5 py-2
              text-sm font-semibold text-white hover:bg-glk-green/90 transition-colors"
          >
            <Plus size={15} /> Add
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-white/40 text-sm py-10 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
          <Card className="!p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  {['Code', 'Name', 'Timezone', 'Type', 'Access', 'Status', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-white/40">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stations.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelected(s.code)}
                    aria-selected={selected === s.code}
                    className={`border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/[0.03] ${selected === s.code ? 'bg-white/[0.05]' : ''}`}
                  >
                    <td className="px-4 py-3 font-mono font-semibold text-white tracking-wider">{s.code}</td>
                    <td className="px-4 py-3 text-white/80">{s.name}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-white/50">{s.timezone}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-white/60">
                        {s.kind === 'office' ? <Building2 size={12} /> : <Plane size={12} />}
                        {s.kind}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-white/60" data-testid="station-access-count">
                      {(() => { const n = users.filter((u) => canSeeStation(u, s.code)).length; return `${n} ${n === 1 ? 'user' : 'users'}`; })()}
                      <span className="text-white/35">
                        {' '}· {users.filter((u) => reachOf(u) !== 'all' && reachOf(u).length > 1 && canSeeStation(u, s.code)).length} regional
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${s.is_active ? 'text-emerald-400' : 'text-white/35'}`}>
                        {s.is_active ? 'Active' : 'Closed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditing(s); }}
                        className="text-xs text-white/40 hover:text-white transition-colors"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {stations.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-white/35">
                      No stations yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>

          {selected && (
            <Card className="mt-4" >
              <h2 className="text-sm font-semibold text-white">Who can see {selected}</h2>
              <ul className="mt-3 divide-y divide-white/5" data-testid="station-access-list">
                {users.filter((u) => canSeeStation(u, selected)).map((u) => (
                  <li key={u.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-white/80">{u.name}
                      <span className="block text-[11px] text-white/40">{u.role.replace(/_/g, ' ')}</span>
                    </span>
                    <span className="text-[11px] font-mono text-white/50">
                      {reachOf(u) === 'all' ? 'All stations' : u.home_station === selected ? 'Home' : 'Regional'}
                    </span>
                  </li>
                ))}
                {users.filter((u) => canSeeStation(u, selected)).length === 0 && (
                  <li className="py-2 text-sm text-white/35">Nobody yet.</li>
                )}
              </ul>
            </Card>
          )}
          </>
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <StationModal
            initial={editing.id ? editing : null}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
